import React, { useState } from "react";
import { Download, ChevronUp, Save, Check } from "lucide-react";

export const SelectionSummaryBar = ({
  totals,
  activePreset,
  isModified,
  isExporting,
  onCancel,
  onExportFull,
  onExportFiltered,
  onSaveAsNew,
  onUpdateActivePreset,
  currentTheme,
  theme,
  isRTL,
  t,
  screenSize,
}) => {
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const isMobile = screenSize === "xs" || screenSize === "sm";

  const hasColumnsSelected = totals.totalSelected > 2;

  const handleUpdate = async () => {
    setShowSaveMenu(false);
    if (activePreset) {
      await onUpdateActivePreset(activePreset.id);
    }
  };

  const handleSaveAsNew = () => {
    setShowSaveMenu(false);
    onSaveAsNew();
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: isMobile ? "wrap" : "nowrap",
        gap: "14px",
        padding: "16px 24px",
        borderTop: `1px solid ${currentTheme.colors.borderLight}`,
        background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
      }}
    >
      {/* Live column summary count */}
      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        <div style={{ fontSize: "0.86rem", fontWeight: 700, color: currentTheme.colors.textPrimary }}>
          {totals.totalSelected} of {totals.maxTotalColumns} columns
        </div>
        <div style={{ fontSize: "0.75rem", color: currentTheme.colors.textSecondary }}>
          {totals.allowanceCols} allowance · {totals.deductionCols} deduction · 2 required
        </div>
        {!hasColumnsSelected && (
          <div style={{ fontSize: "0.74rem", color: currentTheme.colors.warning, fontWeight: 600 }}>
            {t("noColumnsSelected") || "Select at least one column to export."}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: isMobile ? "wrap" : "nowrap",
          width: isMobile ? "100%" : "auto",
          justifyContent: isMobile ? "stretch" : "flex-end",
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: "9px 14px",
            borderRadius: "8px",
            border: `1px solid ${currentTheme.colors.borderLight}`,
            background: "transparent",
            color: currentTheme.colors.textSecondary,
            fontSize: "0.82rem",
            cursor: "pointer",
            flex: isMobile ? "1" : "none",
          }}
        >
          {t("cancel") || "Cancel"}
        </button>

        <button
          type="button"
          onClick={onExportFull}
          disabled={isExporting}
          style={{
            padding: "9px 14px",
            borderRadius: "8px",
            border: `1px solid ${currentTheme.colors.border}`,
            background: theme === "dark" ? "rgba(255,255,255,0.05)" : "#f8fafc",
            color: currentTheme.colors.textPrimary,
            fontSize: "0.82rem",
            fontWeight: 600,
            cursor: isExporting ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
            flex: isMobile ? "1" : "none",
          }}
        >
          {t("exportFullTemplate") || "Export Full Template"}
        </button>

        {/* Save Preset Dropdown / Split Button */}
        <div style={{ position: "relative", flex: isMobile ? "1" : "none" }}>
          <button
            type="button"
            onClick={() => setShowSaveMenu((prev) => !prev)}
            style={{
              padding: "9px 12px",
              borderRadius: "8px",
              border: `1px solid ${currentTheme.colors.border}`,
              background: "transparent",
              color: currentTheme.colors.textPrimary,
              fontSize: "0.82rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              width: "100%",
              justifyContent: "center",
            }}
          >
            <Save size={14} />
            <span>{t("savePreset") || "Save preset"}</span>
            <ChevronUp
              size={14}
              style={{
                transform: showSaveMenu ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 0.15s ease",
              }}
            />
          </button>

          {showSaveMenu && (
            <div
              style={{
                position: "absolute",
                bottom: "calc(100% + 6px)",
                right: isRTL ? "auto" : 0,
                left: isRTL ? 0 : "auto",
                width: "200px",
                background: theme === "dark" ? "#1e293b" : "#ffffff",
                border: `1px solid ${currentTheme.colors.border}`,
                borderRadius: "10px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                padding: "6px",
                zIndex: 50,
                display: "flex",
                flexDirection: "column",
                gap: "2px",
              }}
            >
              {activePreset && isModified && (
                <button
                  type="button"
                  onClick={handleUpdate}
                  style={{
                    padding: "8px 10px",
                    borderRadius: "6px",
                    border: "none",
                    background: "transparent",
                    color: currentTheme.colors.textPrimary,
                    fontSize: "0.8rem",
                    textAlign: isRTL ? "right" : "left",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <Check size={14} color={currentTheme.colors.primary} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    Update "{activePreset.name}"
                  </span>
                </button>
              )}
              <button
                type="button"
                onClick={handleSaveAsNew}
                style={{
                  padding: "8px 10px",
                  borderRadius: "6px",
                  border: "none",
                  background: "transparent",
                  color: currentTheme.colors.textPrimary,
                  fontSize: "0.8rem",
                  textAlign: isRTL ? "right" : "left",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Save size={14} />
                <span>{t("saveAsNew") || "Save as new…"}</span>
              </button>
            </div>
          )}
        </div>

        {/* Primary Filtered Export Button */}
        <button
          type="button"
          onClick={onExportFiltered}
          disabled={isExporting || !hasColumnsSelected}
          style={{
            padding: "9px 18px",
            borderRadius: "8px",
            border: "none",
            background: currentTheme.colors.primary,
            color: "#fff",
            fontSize: "0.84rem",
            fontWeight: 700,
            cursor: isExporting || !hasColumnsSelected ? "not-allowed" : "pointer",
            opacity: isExporting || !hasColumnsSelected ? 0.6 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            flex: isMobile ? "1 1 100%" : "none",
            boxShadow: hasColumnsSelected ? "0 2px 8px rgba(46, 124, 186, 0.3)" : "none",
          }}
        >
          <Download size={15} />
          <span>{isExporting ? (t("saving") || "Exporting...") : (t("exportSelection") || "Export")}</span>
        </button>
      </div>
    </div>
  );
};
